import {
  useEffect,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Capacitor,
  registerPlugin
} from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import {
  setPosts,
  getComments,
  getPosts,
  setOpenPopupBlogs,
  setBlogParent,
  usersSelectors,
  saveNtfyTopic,
  setUserSectionPage,
  setOpenPopupEvents,
  setIsBlogFormOpen,
  setOpenBlogForm,
  setChatSectionPage,
  setNotificationSectionPage,
  setBlogRepostData,
  handleOpenModal,
  getNotificationMessage,
  setEventComments,
  setEventCommentTree,
  getEventData,
  setGetProfileItemStatus,
  setOpenFriendRequest,
  setOpenNotifRequest
} from 'store'
import { openConnectedProfileChat } from 'store/chat/slice'
import {
  MODAL_SECTION,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { useGoToProfileMarker } from 'hooks/useGoToProfileMarker'

const PushDevice = registerPlugin('PushDevice')

export const useNfty = ([
  signInStatus,
  isAuth
]) => {
  const dispatch = useDispatch()

  const goToProfileMarker = useGoToProfileMarker()
  const [pushNotifAction, setPushNotifAction] = useState([])

  const {
    activeProfile,
    userProfile
  } = useSelector(usersSelectors)

  const clearUI = () => {
    dispatch(setUserSectionPage('clear'))
    dispatch(setIsBlogFormOpen(false))
    dispatch(setOpenBlogForm(false))
    document.querySelector('#custom-popup-overlay')?.click()
    document.querySelector('#custom-popup-overlay-event')?.click()
    dispatch(setOpenPopupBlogs(false))
    dispatch(setChatSectionPage('clear'))
    dispatch(setOpenPopupEvents(false))
    dispatch(setNotificationSectionPage('clear'))
    dispatch(setBlogRepostData([]))
    dispatch(handleOpenModal({ isOpen: false, section: MODAL_SECTION?.RightSection }))
    dispatch(setBlogParent([]))
    dispatch(setPosts([]))
  }

  const initialize = async () => {
    if (Capacitor.isNativePlatform()) {
      await PushDevice.removeAllListeners()

      const { topic } = await PushDevice.getDeviceInfo()
      if (topic) {
        dispatch(saveNtfyTopic(topic))
      }

      let permStatus = await PushNotifications.checkPermissions()
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions()
      }

      if (permStatus.receive !== 'granted') {
        throw new Error('Permission not granted')
      }

      PushDevice.addListener('pushTapPerformed', (action) => {
        const { data } = action
        setPushNotifAction(data)
      })

      // Cold-start case: the tap that launched the app fired before this
      // listener existed to catch it. Pull it explicitly, once, here.
      const pending = await PushDevice.getPendingPushTap()
      if (pending?.data) {
        setPushNotifAction(pending.data)
      }
    }
  }

  useEffect(() => {
    if (
      pushNotifAction?.type === 'profile'
      && userProfile?.length
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      goToProfileMarker({
        profileId: actionData?.id,
        coordinates: JSON.parse(actionData?.coordinates)
      })
    }
  }, [userProfile, pushNotifAction])

  useEffect(() => {
    if (
      pushNotifAction?.type === 'post'
      && activeProfile?.id
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      dispatch(getPosts({
        type: 'single',
        postId: actionData?.id,
        id: activeProfile?.id
      }))
      dispatch(setOpenPopupBlogs(true))
    }
  }, [activeProfile?.id, pushNotifAction])

  useEffect(() => {
    if (
      pushNotifAction?.type === 'comment'
      && activeProfile?.id
    ) {
      const run = async () => {
        clearUI()
        const actionData = pushNotifAction
        setPushNotifAction([])

        dispatch(setOpenPopupBlogs(true))
        const result = await dispatch(getPosts({
          type: 'single',
          postId: actionData?.post_id,
          id: activeProfile?.id
        }))
        const postResults = result?.payload?.posts

        if (postResults?.length) {
          const path = JSON.parse(actionData?.path)

          dispatch(setBlogParent([
            postResults?.at(0),
            ...path
          ]))

          dispatch(getComments({
            type: actionData?.parent_type,
            forceGet: actionData?.id,
            ...(
              actionData?.parent_type === 'post'
                ? { post_id: actionData?.post_id }
                : { comment_id: path?.at(-1)?.comment_id }
            )
          }))
        } else {
          dispatch(
            getNotificationMessage({
              type: REJECTED,
              message: 'This article might be deleted, not existing, or unavailable',
              isEditProfile: false
            })
          )
        }
      }

      run()
    } else if (
      pushNotifAction?.type === 'event'
      && activeProfile?.id
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      dispatch(setEventComments([]))
      dispatch(setEventCommentTree([]))
      dispatch(getEventData(actionData?.id))
      dispatch(setOpenPopupEvents(true))
    } else if (
      pushNotifAction?.type === 'connReq'
      && activeProfile?.id
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      dispatch(setGetProfileItemStatus(0))
      dispatch(setOpenFriendRequest(actionData?.id))
      setTimeout(() => document.querySelector('#open-notif-panel').click(), 800)
    } else if (
      pushNotifAction?.type === 'notif'
      && activeProfile?.id
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      dispatch(setGetProfileItemStatus(0))
      dispatch(setOpenNotifRequest(actionData?.id))
      setTimeout(() => document.querySelector('#open-notif-panel').click(), 800)
    } else if (
      pushNotifAction?.type === 'chat'
      && activeProfile?.id
    ) {
      clearUI()
      const actionData = pushNotifAction
      setPushNotifAction([])

      setTimeout(() => {
        dispatch(setChatSectionPage('chat-main'))
        dispatch(openConnectedProfileChat({ id: actionData?.id }))
      }, 900)
    }
  }, [activeProfile?.id, pushNotifAction])

  useEffect(() => {
    if (
      signInStatus === SUCCEEDED
      || isAuth()
    ) {
      initialize()
    }
  }, [isAuth, signInStatus, activeProfile?.id])

  return {}
}
